import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { HOUSE } from "@/constants/testIds";
import { submitInquiry } from "@/lib/api";

const INQUIRY_TYPES = [
  { value: "commercial", label: "Commercial Inquiry" },
  { value: "licensing", label: "Licensing & Strategic Partnership" },
  { value: "design", label: "Design & Architectural Collaboration" },
  { value: "pilot", label: "Pilot Opportunity" },
  { value: "media", label: "Media & Press" },
  { value: "general", label: "General Correspondence" },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const Inquiry = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    organization: "",
    role: "",
    inquiry_type: "commercial",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      await submitInquiry({ ...form, source: "abike-inquiry" });
      setStatus("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Something went wrong. Please try again.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        if (first?.msg) {
          msg = /email/i.test(first.msg)
            ? "Please enter a valid email address."
            : first.msg;
        }
      }
      setErrorMsg(msg);
      setStatus("error");
    }
  };

  const isDone = status === "success";
  const isBusy = status === "loading" || isDone;

  const inputCls =
    "w-full bg-transparent border-0 border-b border-[var(--border-dark)] focus:border-[var(--gold-light)] text-[var(--ivory)] placeholder:text-[var(--muted-dark)] pb-3 pt-1 text-base outline-none transition-colors font-sans disabled:opacity-60";
  const labelCls =
    "block text-[10px] uppercase tracking-[0.32em] text-[var(--muted-dark)] mb-3 font-sans";

  return (
    <section
      id="inquiry"
      data-testid={HOUSE.sectionInquiry}
      className="relative bg-[var(--black)] py-28 md:py-40 border-t border-[var(--border-dark)]"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20">
        {/* Intro */}
        <motion.div {...fadeUp} className="md:col-span-5">
          <p className="font-sans text-[11px] uppercase tracking-[0.5em] text-[var(--gold-light)] mb-8">
            Correspondence
          </p>
          <h2 className="font-serif text-[var(--ivory)] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
            Bring Abike
            <br />
            <span className="italic text-[var(--gold-light)]">
              into your environment.
            </span>
          </h2>
          <p className="mt-8 font-sans text-[var(--stone)] leading-relaxed max-w-md">
            The House of BorsaLux welcomes commercial inquiries, licensing and
            strategic partnerships, design collaboration, pilot opportunities,
            and media correspondence.
          </p>
          <div className="mt-10 space-y-2 font-sans text-sm text-[var(--muted-dark)]">
            <div className="flex items-center gap-3">
              <span className="h-px w-6 bg-[var(--gold)]/50" />
              <a
                href="mailto:telesa@borsalux.com"
                className="text-[var(--ivory)] hover:text-[var(--gold-light)] transition-colors"
              >
                telesa@borsalux.com
              </a>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          className="md:col-span-7"
        >
          {isDone ? (
            <div
              data-testid={HOUSE.inquirySuccess}
              className="border border-[var(--gold)]/40 p-10 md:p-14 bg-[var(--walnut-deep)]"
            >
              <div className="w-12 h-12 border border-[var(--gold-light)] flex items-center justify-center text-[var(--gold-light)] mb-6">
                <Check size={18} strokeWidth={1.6} />
              </div>
              <p className="font-sans text-[10px] uppercase tracking-[0.5em] text-[var(--gold-light)] mb-4">
                Received
              </p>
              <p className="font-serif text-[var(--ivory)] text-2xl md:text-3xl leading-snug tracking-tight max-w-md">
                Thank you. Your inquiry has arrived at The House of BorsaLux.
              </p>
              <p className="mt-6 font-sans text-[var(--stone)]/80 max-w-md">
                Telesa will be in touch personally.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label htmlFor={HOUSE.inquiryFirstName} className={labelCls}>
                    First Name
                  </label>
                  <input
                    id={HOUSE.inquiryFirstName}
                    data-testid={HOUSE.inquiryFirstName}
                    type="text"
                    required
                    value={form.first_name}
                    onChange={(e) => set("first_name", e.target.value)}
                    placeholder=""
                    disabled={isBusy}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor={HOUSE.inquiryLastName} className={labelCls}>
                    Last Name
                  </label>
                  <input
                    id={HOUSE.inquiryLastName}
                    data-testid={HOUSE.inquiryLastName}
                    type="text"
                    value={form.last_name}
                    onChange={(e) => set("last_name", e.target.value)}
                    placeholder=""
                    disabled={isBusy}
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label htmlFor={HOUSE.inquiryEmail} className={labelCls}>
                    Email
                  </label>
                  <input
                    id={HOUSE.inquiryEmail}
                    data-testid={HOUSE.inquiryEmail}
                    type="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@example.com"
                    disabled={isBusy}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor={HOUSE.inquiryOrganization} className={labelCls}>
                    Organization
                  </label>
                  <input
                    id={HOUSE.inquiryOrganization}
                    data-testid={HOUSE.inquiryOrganization}
                    type="text"
                    value={form.organization}
                    onChange={(e) => set("organization", e.target.value)}
                    placeholder="Company / Studio / Group"
                    disabled={isBusy}
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label htmlFor={HOUSE.inquiryRole} className={labelCls}>
                    Role
                  </label>
                  <input
                    id={HOUSE.inquiryRole}
                    data-testid={HOUSE.inquiryRole}
                    type="text"
                    value={form.role}
                    onChange={(e) => set("role", e.target.value)}
                    placeholder="Architect · Owner · Developer"
                    disabled={isBusy}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor={HOUSE.inquiryType} className={labelCls}>
                    Reason for Inquiry
                  </label>
                  <select
                    id={HOUSE.inquiryType}
                    data-testid={HOUSE.inquiryType}
                    value={form.inquiry_type}
                    onChange={(e) => set("inquiry_type", e.target.value)}
                    disabled={isBusy}
                    className={`${inputCls} appearance-none bg-[var(--black)] cursor-pointer`}
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option
                        key={t.value}
                        value={t.value}
                        className="bg-[var(--black)] text-[var(--ivory)]"
                      >
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor={HOUSE.inquiryMessage} className={labelCls}>
                  Message
                </label>
                <textarea
                  id={HOUSE.inquiryMessage}
                  data-testid={HOUSE.inquiryMessage}
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="Tell us about your environment, project, or interest."
                  disabled={isBusy}
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  data-testid={HOUSE.inquirySubmit}
                  disabled={isBusy}
                  className="group inline-flex items-center gap-4 bg-[var(--ivory)] text-[var(--black)] font-sans uppercase tracking-[0.32em] text-[11px] px-10 py-5 hover:bg-[var(--gold-light)] transition-all duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? (
                    <>
                      Sending<span className="animate-pulse">…</span>
                    </>
                  ) : (
                    <>
                      Send Inquiry
                      <ArrowRight
                        size={14}
                        strokeWidth={1.6}
                        className="transition-transform duration-500 group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
              </div>

              <AnimatePresence>
                {status === "error" && (
                  <motion.p
                    data-testid={HOUSE.inquiryError}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-sm text-red-400/90 font-sans"
                  >
                    {errorMsg}
                  </motion.p>
                )}
              </AnimatePresence>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
